/* Copyright Information: Created by BuyGroup */
import React, { useState, useEffect } from "react";
import { Formik, Form, Field, ErrorMessage } from "formik";
import {
  Button,
  FormGroup,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
} from "reactstrap";

const OrderUpdate = (props) => {
  const [selectedValue, setSelectedValue] = useState("");
  const [showTracking, setShowTracking] = useState(
    props.rowData.status === "shipping"
  );

  const canUpdateStatus =
    props.rowData.can_update_status !== undefined
      ? props.rowData.can_update_status === true
      : !["payment_failed", "amazon_submit_failed"].includes(props.rowData.status);

  // Check if the selected value is the same as current status
  const isSameStatus = !canUpdateStatus || selectedValue === props.rowData.status || selectedValue === "";

  // Define the status flow - what statuses can transition to what
  const getAvailableStatuses = (currentStatus) => {
    const statusFlow = {
      // Regular order flow
      in_progress: ["shipping", "cancelled", "payment_failed"],
      shipping: ["completed", "cancelled", "payment_failed"],
      order_placed: ["completed", "cancelled", "shipping"],
      completed: [],
      cancelled: [],
      paid: ["completed", "cancelled"],
      payment_failed: [],
      
      // Return flow - controlled workflow
      return_requested: ["return_accepted", "return_declined"],
      return_accepted: ["return_in_progress", "return_label_sent", "refunded"],
      return_declined: [],
      return_in_progress: ["refunded" , "return_label_sent"],
      refunded: [],
      return_label_sent: [ "refunded" ],
      payment_captured: [],
      amazon_submit_pending: [],
      amazon_submitted: ["completed", "cancelled", "shipping"],
      amazon_submit_failed: [],
    };

    return statusFlow[currentStatus] || [];
  };

  // Get all available status options for the current order
  const getStatusOptions = () => {
    const currentStatus = props.rowData.status;
    const availableStatuses = getAvailableStatuses(currentStatus);
    
    const allStatuses = {
      in_progress: "In Progress",
      shipping: "To Be Shipped", 
      completed: "Completed",
      cancelled: "Cancelled",
      paid: "Paid",
      payment_failed: "Payment Failed",
      return_requested: "Return Requested",
      return_accepted: "Accept Return",
      return_declined: "Decline Return", 
      return_in_progress: "Return In Progress",
      refunded: "Refunded",
      return_label_sent: "Return Label Sent",
      payment_captured: "Payment Captured",
      amazon_submit_pending: "Amazon Submit Pending",
      amazon_submitted: "Submitted to Amazon",
      amazon_submit_failed: "Amazon Submit Failed"
    };

    // Start with empty option
    const options = [
      { value: "", label: "Select Status" }
    ];

    if (canUpdateStatus) {
      // Add available transition statuses
      availableStatuses.forEach(status => {
        options.push({
          value: status,
          label: allStatuses[status]
        });
      });
    }

    return options;
  };

  /* closed update order modal */
  const closeHandler = () => {
    props.handleModalClose();
  };

  /* handle status of order  */
  const statusHandleChange = (e, setFieldValue) => {
    setSelectedValue(e.target.value);
    setShowTracking(e.target.value === "shipping");
    setFieldValue("status", e.target.value);
  };

  return (
    <div>
      <Modal
        isOpen={props.orderUpdateModal}
        toggle={closeHandler}
        style={{ overlay: { opacity: 0.1 } }}
      >
        <ModalHeader toggle={closeHandler}>
          <p className="modal-title f-w-600">Update Order</p>
        </ModalHeader>

        <Formik
          initialValues={{
            order_line_id: props.rowData.order_line_id,
            status: props.rowData.status,
            tracking_number: props.rowData.tracking_number,
            tracking_company_name: props.rowData.tracking_company_name,
          }}
          onSubmit={(values, actions) => {
            props.onSubmit(values, actions);
          }}
        >
          {({
            values,
            errors,
            touched,
            handleChange,
            handleBlur,
            handleSubmit,
            isSubmitting,
            resetForm,
            setFieldValue,
          }) => (
            <Form>
              <ModalBody>
                <input
                  type="hidden"
                  className="form-control"
                  name="order_line_id"
                  value={values.order_line_id}
                />
                
                {/* Current Status Display */}
                <div className="mb-3">
                  <strong>Current Status:</strong> 
                  <span className={`ml-2 status_column ${props.rowData.status}`}>
                    {(() => {
                      const allStatuses = {
                        in_progress: "In Progress",
                        shipping: "To Be Shipped", 
                        completed: "Completed",
                        cancelled: "Cancelled",
                        paid: "Paid",
                        payment_failed: "Payment Failed",
                        return_requested: "Return Requested",
                        return_accepted: "Return Accepted",
                        return_declined: "Return Declined", 
                        return_in_progress: "Return In Progress",
                        refunded: "Refunded",
                        return_label_sent: "Return Label Sent",
                        order_placed: "Order Placed",
                        payment_captured: "Payment Captured",
                        amazon_submit_pending: "Amazon Submit Pending",
                        amazon_submitted: "Submitted to Amazon",
                        amazon_submit_failed: "Amazon Submit Failed"
                      };
                      return allStatuses[props.rowData.status] || props.rowData.status;
                    })()}
                  </span>
                </div>

                <FormGroup>
                  <Label className="col-form-label">Status</Label>
                  {!canUpdateStatus && (
                    <p className="text-muted mb-2">
                      Failed order lines cannot be updated from admin.
                    </p>
                  )}
                  <select
                    name="status"
                    className="form-control"
                    value={selectedValue}
                    onChange={(e) => statusHandleChange(e, setFieldValue)}
                    onBlur={handleBlur}
                    disabled={!canUpdateStatus}
                  >
                    {getStatusOptions().map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>

                  <ErrorMessage name="status">
                    {(errorMsg) => (
                      <label className="err-msg">{errorMsg}</label>
                    )}
                  </ErrorMessage>
                </FormGroup>
                {showTracking ? (
                  <>
                    <FormGroup>
                      <Label className="col-form-label">
                        Add Tracking Code
                      </Label>
                      <input
                        type="text"
                        className="form-control"
                        name="tracking_number"
                        onChange={handleChange}
                        onBlur={handleBlur}
                        value={values.tracking_number}
                      />
                    </FormGroup>
                    <FormGroup>
                      <Label className="col-form-label">Company Name</Label>
                      <input
                        type="text"
                        className="form-control"
                        name="tracking_company_name"
                        onChange={handleChange}
                        onBlur={handleBlur}
                        value={values.tracking_company_name}
                      />
                    </FormGroup>
                  </>
                ) : null}
              </ModalBody>

              <ModalFooter>
                <Button
                  color="primary"
                  type="submit"
                  disabled={props.isLoadingButton || isSameStatus}
                >
                  {props.isLoadingButton ? "Please Wait..." : "Update"}
                </Button>

                <Button type="button" color="light" onClick={closeHandler}>
                  Cancel
                </Button>
              </ModalFooter>
            </Form>
          )}
        </Formik>
      </Modal>
    </div>
  );
};

export default OrderUpdate;
/* Copyright Information: Created by BuyGroup */
